import { Suspense, lazy, useEffect } from 'react'
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { PublicLayout, LoginModal } from './components/PublicLayout'
import { AdminLayout } from './components/AdminLayout'
import { RouteError } from './components/RouteError'
import { PageTracker } from './components/PageTracker'
import { Spinner, Toasts } from './components/ui'
import { useApp } from './store/AppProvider'

const Home = lazy(() => import('./pages/Home'))
const Catalog = lazy(() => import('./pages/Catalog'))
const CourseDetail = lazy(() => import('./pages/CourseDetail'))
const Checkout = lazy(() => import('./pages/Checkout'))
const Providers = lazy(() => import('./pages/Providers'))
const ProviderDetail = lazy(() => import('./pages/ProviderDetail'))
const InstructorDetail = lazy(() => import('./pages/InstructorDetail'))
const Instructors = lazy(() => import('./pages/Instructors'))
const Account = lazy(() => import('./pages/account/Account'))
const LiveClasses = lazy(() => import('./pages/LiveClasses'))
const LiveRoom = lazy(() => import('./pages/LiveRoom'))
const QA = lazy(() => import('./pages/QA'))
const QADetail = lazy(() => import('./pages/QADetail'))
const Consultation = lazy(() => import('./pages/Consultation'))
const Verify = lazy(() => import('./pages/Verify'))
const AuthCallback = lazy(() => import('./pages/AuthCallback'))

const MyLearning = lazy(() => import('./pages/student/MyLearning'))
const Player = lazy(() => import('./pages/student/Player'))
const Certificates = lazy(() => import('./pages/student/Certificates'))
const Bookings = lazy(() => import('./pages/student/Bookings'))
const Orders = lazy(() => import('./pages/student/Orders'))

const Dashboard = lazy(() => import('./pages/admin/Dashboard'))
const ResourcePage = lazy(() => import('./pages/admin/ResourcePage'))
const CourseEditor = lazy(() => import('./pages/admin/CourseEditor'))
const Broadcast = lazy(() => import('./pages/admin/Broadcast'))
const Guide = lazy(() => import('./pages/admin/Guide'))


const Loading = () => <div className="py-24 grid place-items-center text-muted"><Spinner size={26} /></div>

function ScrollTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  return null
}

/**
 * Auth gate: opens the login modal instead of a hard redirect.
 *
 * `role` additionally restricts the route to one role. Logged in but wrong
 * role renders an explanation rather than re-opening the login modal — the user
 * is already signed in, so asking them to sign in again is a dead end.
 */
function Protected({ children, role }) {
  const { isAuthed, user, setLoginOpen } = useApp()
  useEffect(() => { if (!isAuthed) setLoginOpen(true) }, [isAuthed, setLoginOpen])
  if (!isAuthed) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 py-24 text-center">
        <p className="text-lg font-extrabold">Halaman ini butuh login</p>
        <p className="text-muted text-sm mt-1">Masuk dengan akun The Muslim untuk melanjutkan.</p>
      </div>
    )
  }
  if (role && user?.role !== role) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 py-24 text-center">
        <p className="text-lg font-extrabold">Halaman ini bukan untuk peran akun Anda</p>
        <p className="text-muted text-sm mt-1">Kembali ke <Link className="text-brand font-bold" to="/">beranda</Link>.</p>
      </div>
    )
  }
  return children
}

export default function App() {
  return (
    <>
      <ScrollTop />
      <PageTracker />
      <Routes>
        {/* Public storefront — PublicLayout mounts once, only <Outlet/> swaps */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/courses" element={<Catalog />} />
          <Route path="/courses/:slug" element={<CourseDetail />} />
          <Route path="/checkout/:slug" element={<Protected><Checkout /></Protected>} />
          <Route path="/providers" element={<Providers />} />
          <Route path="/p/:slug" element={<ProviderDetail />} />
          <Route path="/instructors" element={<Instructors />} />
          <Route path="/instructor/:slug" element={<InstructorDetail />} />
          <Route path="/live" element={<LiveClasses />} />
          <Route path="/live/:id" element={<LiveRoom />} />
          <Route path="/qa" element={<QA />} />
          <Route path="/qa/:id" element={<QADetail />} />
          <Route path="/consultation" element={<Consultation />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/verify/:code" element={<Verify />} />

          {/* Tempat mendarat setelah login di The Muslim. Nilainya HARUS sama
              persis dengan OIDC_REDIRECT_URI di server Go dan dengan
              redirect_uris pada baris Application di Django — server otorisasi
              mencocokkannya karakter per karakter, dan yang tidak cocok
              ditolak sebelum halaman ini sempat dimuat. */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Auth-only, NOT instructor-only: the Security tab (change password)
              is for every signed-in role. The Profile tab gates itself. */}
          <Route path="/me/profile" element={<Protected><Account /></Protected>} />
          <Route path="/me" element={<Protected><MyLearning /></Protected>} />
          <Route path="/me/certificates" element={<Protected><Certificates /></Protected>} />
          <Route path="/me/bookings" element={<Protected><Bookings /></Protected>} />
          <Route path="/me/orders" element={<Protected><Orders /></Protected>} />
          {/* Dompet, afiliasi, dan pencairan terbuka untuk SEMUA peran yang
              login, bukan hanya pakar. Siapa pun bisa jadi afiliator, dan
              siapa pun yang membeli kelas punya dompet. Membatasinya ke satu
              peran akan menyembunyikan uang milik orang dari orangnya. */}
        </Route>

        {/* Full-screen player — no chrome.

            SENGAJA tanpa <Protected>. Pelajaran bertanda "cicip gratis" memang
            boleh ditonton tanpa akun — server sudah menegakkannya sendiri
            (LessonDetail membalas 403 untuk pelajaran yang bukan pratinjau).
            Menambahkan gerbang di sini menutup lebih rapat daripada aturannya:
            tombol "Cicip gratis" di halaman kelas terbuka, lalu mendarat di
            dinding login — dan yang hilang justru satu-satunya cara calon murid
            menilai isi kelas sebelum membayar.

            Batasnya tetap di server. Halaman ini cuma memilih kalimat apa yang
            ditampilkan saat server menolak. */}
        <Route path="/learn/:lessonId" element={
          <RouteError><Suspense fallback={<Loading />}><Player /></Suspense></RouteError>
        } />

        {/* Admin portal — AdminLayout mounts once, sidebar persists across pages */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="panduan" element={<Guide />} />
          <Route path="courses" element={<ResourcePage resource="courses" />} />
          <Route path="courses/:id" element={<CourseEditor />} />
          <Route path="instructors" element={<ResourcePage resource="instructors" />} />
          <Route path="live-classes" element={<ResourcePage resource="live-classes" />} />
          <Route path="qa" element={<ResourcePage resource="qa" />} />
          <Route path="consultations" element={<ResourcePage resource="consultations" />} />
          <Route path="consultation-slots" element={<ResourcePage resource="consultation-slots" />} />
          <Route path="enrollments" element={<ResourcePage resource="enrollments" />} />
          <Route path="reviews" element={<ResourcePage resource="reviews" />} />
          <Route path="certificates" element={<ResourcePage resource="certificates" />} />
          <Route path="coupons" element={<ResourcePage resource="coupons" />} />
          <Route path="notifications" element={<Broadcast />} />
          <Route path="providers" element={<ResourcePage resource="providers" />} />
          <Route path="categories" element={<ResourcePage resource="categories" />} />
          <Route path="certificate-templates" element={<ResourcePage resource="certificate-templates" />} />
          <Route path="audit-logs" element={<ResourcePage resource="audit-logs" />} />
          {/* Uang platform. Gerbang keduanya ada di server (hanya admin
              platform); di sini menunya sudah disaring, dan halamannya sendiri
              menampilkan penolakan yang jelas kalau tetap dibuka lewat URL. */}
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <LoginModal />
      <Toasts />
    </>
  )
}
